import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {InquiryIntroComponent} from '../inquiry-intro/inquiry-intro.component';
import {InquiryService} from '../../service/inquiry.service';
import {RemoveLeadingZerosPipe} from '../../../../shared/pipes/removeLeadingZeros.pipe';
import {PageId} from '../../enum/inquiry-page.enum';
import {InquiryContactPanelComponent} from '../inquiry-contact-panel/inquiry-contact-panel.component';
import {InquiryOrderSelectPanelComponent} from '../inquiry-order-select-panel/inquiry-order-select-panel.component';
import {mapReportLineDetailsToInquiryOrder} from '../../../../shared/mapper/order.mapper';
import {InquiryOrder, InquiryQuestion} from '../../data-access/interface/inquiry.interface';
import {Topic} from '../../enum/topic.enum';
import {DragDropDirective} from '../../../../shared/directives/file-drag-drop.directive';
import {AbstractSidePanelComponent} from '../../../../shared/ui/side-panel/abstract-side-panel.component';
import {SidePanelCloseReason} from '../../../../shared/enum/side-panel-close-reason.enum';
import {SoldToShipToService} from '../../../../shared/services/sold-to-ship-to.service';
import {IconName} from '@cov/ui-angular';
import {SidePanelService} from '../../../../shared/services/side-panel.service';
import {SIDE_PANEL_DATA} from '../../../../shared/token/side-panel.token';
import {InquirySidePanelDataInterface} from '../../data-access/interface/inquiry-sidepanel-data.interface';
import {ReportLineDetailData} from '../../../../interfaces/report-line-details.interface';

@Component({
  selector: 'cov-inquiry-side-drawer',
  imports: [
    CommonModule,
    InquiryIntroComponent,
    RemoveLeadingZerosPipe,
    InquiryContactPanelComponent,
    InquiryOrderSelectPanelComponent,
  ],
  providers: [DragDropDirective],
  templateUrl: './inquiry-side-drawer.component.html',
  styleUrl: './inquiry-side-drawer.component.scss',
})
export class InquirySideDrawerComponent
  extends AbstractSidePanelComponent
  implements OnInit, OnDestroy
{
  // CONST
  protected readonly PageId = PageId;
  private readonly existingPages = [PageId.INTRO, PageId.ORDER_SELECT, PageId.INQUIRY_FORM];

  // INJECTS
  protected inquiryService: InquiryService = inject(InquiryService);
  protected soldToService: SoldToShipToService = inject(SoldToShipToService);
  protected sidePanelService: SidePanelService = inject(SidePanelService);
  protected data: InquirySidePanelDataInterface =
    inject<InquirySidePanelDataInterface>(SIDE_PANEL_DATA);

  // STATE
  protected currentPage = signal<PageId>(PageId.INTRO);
  protected userSelectedOrder: WritableSignal<InquiryOrder | null> = signal(null);
  protected ignoreOrder: WritableSignal<boolean> = signal(false);

  protected activePage = computed(
    () => new Map(this.existingPages.map((id) => [id, this.currentPage() === id])),
  );

  protected activeOrder = computed(() => {
    if (this.ignoreOrder()) {
      return null;
    }
    return this.userSelectedOrder() ? this.userSelectedOrder() : this.inquiryService.order();
  });

  // METHODS

  public ngOnInit() {
    if (this.data?.page) {
      this.currentPage.set(this.data.page);
    }
    if (this.sidePanelHandler) this.sidePanelService.partlyClosedAvailable.set(true);
  }

  public changePage(question: InquiryQuestion): void {
    // in case user has general question, we ignore orders
    this.ignoreOrder.set(question.topic === Topic.GENERAL_INQUIRY);
    // in case user selects context we remove user selection
    if (question.topic === Topic.ORDER_FROM_CONTEXT) {
      this.userSelectedOrder.set(null);
    }
    this.currentPage.set(question.destination);
  }

  public closeSidePanel(): void {
    this.sidePanelHandler.close({reason: SidePanelCloseReason.DISMISS, data: null});
  }

  public onSelectedOrder(reportLine: ReportLineDetailData): void {
    this.ignoreOrder.set(false);
    this.userSelectedOrder.set(mapReportLineDetailsToInquiryOrder(reportLine));
    this.currentPage.set(PageId.INQUIRY_FORM);
  }

  public ngOnDestroy() {
    this.sidePanelService.partlyClosedAvailable.set(false);
  }

  protected readonly close = close;
  protected readonly IconName = IconName;
}
